/**
 * @module BackButtonBlocker
 * @description A reliable, class-based module to prevent the user from navigating
 * away from the current page using the browser's back button.
 * This version is simplified for stability and reliability.
 */
'use strict';

class BackButtonBlocker {
    /**
     * @param {object} options - Configuration options.
     * @param {function} options.onBackAttempt - A callback function to execute when a back navigation attempt is blocked.
     */
    constructor(options = {}) {
        // The callback function to run when a back attempt is detected.
        this.onBackAttempt = options.onBackAttempt || this.defaultLogger;
        this.isActive = false;

        // Bind the context of 'this' for the event handler.
        this.handlePopState = this.handlePopState.bind(this);
    }

    /**
     * The default callback function if none is provided. Logs to the console.
     */
    defaultLogger() {
        console.log('%c🚫 Back button navigation was prevented.', 'color: #ffcc00; font-weight: bold;');
    }

    /**
     * Activates the back button prevention.
     * This is the only method you need to call to start the blocker.
     */
    activate() {
        if (this.isActive) {
            console.warn('Back button blocker is already active.');
            return;
        }

        // 1. Push an initial state onto the history stack. This is the key.
        // It creates an entry that we can "catch" when the user tries to go back.
        try {
            window.history.pushState(null, '', window.location.href);
            this.isActive = true;
            console.log('✅ Back button blocker has been activated.');
        } catch (e) {
            console.error('Could not push state to history:', e);
            return;
        }

        // 2. Add the event listener for the 'popstate' event.
        window.addEventListener('popstate', this.handlePopState);
    }

    /**
     * Deactivates the back button prevention and cleans up the event listener.
     */
    deactivate() {
        if (!this.isActive) {
            return;
        }
        window.removeEventListener('popstate', this.handlePopState);
        this.isActive = false;
        console.log('❌ Back button blocker has been deactivated.');
    }

    /**
     * This is the core handler that runs when a 'popstate' event occurs.
     * It prevents the navigation and re-adds our history state.
     * @private
     */
    handlePopState() {
        if (!this.isActive) {
            return;
        }
        // When the user clicks back, the popstate event fires. We immediately
        // push our state back onto the history, effectively canceling the back action.
        window.history.pushState(null, '', window.location.href);

        // Execute the user-defined callback.
        this.onBackAttempt();
    }
}

// Export the class for use in other modules.
export { BackButtonBlocker };
export default BackButtonBlocker;